import type { ColDef, ColumnState } from 'ag-grid-community';

/**
 * REG-828: an admin's default columns for a grid whose columns are defined in
 * page code (core_crm.grid_column_default). Precedence on a grid:
 *
 *   1. the page's own column list (which columns exist, renderers, editors,
 *      and which ones a role may see at all);
 *   2. the admin default: order, visibility, width and an optional label for
 *      the columns the page offers;
 *   3. the user's own saved layout (useGridLayout).
 *
 * The admin default is merged into the column definitions before AG Grid sees
 * them, so "Reset columns" (resetColumnState) returns to it. Pure functions
 * only, so the rules can be checked with plain data.
 */

export interface GridColumnDefaultEntry {
  colId: string;
  hide?: boolean;
  width?: number;
  /** Header text that replaces the page's own. */
  label?: string | null;
  /** The page's header text when the default was saved, for the admin screen. */
  header?: string;
}

export interface GridColumnDefault {
  gridKey: string;
  columns: GridColumnDefaultEntry[];
  version: number;
  updatedAt?: string | null;
  updatedBy?: number | null;
}

/**
 * The admin default a user's saved layout was made against: its version (0 for
 * the page's own defaults) and the columns it showed.
 */
export interface GridDefaultsSnapshot {
  version: number;
  visible: string[];
}

/** Grid columns that are controls, not data, and never part of a default. */
export const NON_DATA_COLUMN_IDS: ReadonlySet<string> = new Set(['__row_actions', 'bulk-selection', 'ag-Grid-SelectionColumn']);

/** AG Grid's id for a column definition: its colId, else its field. */
export function columnIdOf(column: ColDef<any>): string | undefined {
  if (column.colId) return column.colId;
  return typeof column.field === 'string' && column.field ? column.field : undefined;
}

/**
 * The page's columns with the admin default applied. Columns the default lists
 * take the default's order, visibility, width and label; they fill, in that
 * order, the positions the listed columns hold in the page's list, so columns
 * the default does not list (a selection checkbox, a column added to the page
 * after the default was saved) keep their own place and settings. Entries for
 * columns the page does not offer (renamed, removed, or not shown to this
 * role) are ignored.
 */
export function applyGridColumnDefaults<T>(
  columns: readonly ColDef<T>[],
  gridDefault: GridColumnDefault | null | undefined,
): ColDef<T>[] {
  if (!gridDefault || gridDefault.columns.length === 0) return [...columns];
  const byId = new Map<string, ColDef<T>>();
  for (const column of columns) {
    const id = columnIdOf(column);
    if (id && !NON_DATA_COLUMN_IDS.has(id) && !byId.has(id)) byId.set(id, column);
  }
  const listed = gridDefault.columns.filter((entry) => byId.has(entry.colId));
  if (listed.length === 0) return [...columns];
  const listedIds = new Set(listed.map((entry) => entry.colId));
  // A page can pass a column id twice (the later definition wins in AG Grid);
  // only its first slot takes part, so slots and entries always pair up.
  const slotted = new Set<string>();
  let next = 0;
  return columns.map((column) => {
    const id = columnIdOf(column);
    if (!id || !listedIds.has(id) || slotted.has(id)) return column;
    slotted.add(id);
    const entry = listed[next];
    next += 1;
    if (!entry) return column;
    const merged: ColDef<T> = { ...byId.get(entry.colId)! };
    if (typeof entry.hide === 'boolean') merged.hide = entry.hide;
    if (typeof entry.width === 'number' && entry.width > 0) merged.width = entry.width;
    if (entry.label) merged.headerName = entry.label;
    return merged;
  });
}

/** Ids of the data columns a column list shows by default, in order. */
export function visibleColumnIds(columns: readonly ColDef<any>[]): string[] {
  return columns
    .filter((column) => !column.hide)
    .map(columnIdOf)
    .filter((id): id is string => Boolean(id) && !NON_DATA_COLUMN_IDS.has(id as string));
}

/**
 * A user's saved layout against the admin default in force now. When the admin
 * default changed since the layout was saved, a column the admin now shows that
 * the default did not show then is shown again, so a newly added column is not
 * silently missing for users who had saved a layout. Everything else the user
 * chose (order, widths, columns they hid themselves) stays as they left it.
 * A layout saved without a snapshot was made against the page's own defaults.
 */
export function reconcileSavedLayout(
  saved: readonly ColumnState[],
  savedAgainst: GridDefaultsSnapshot | null | undefined,
  current: GridDefaultsSnapshot,
  pageDefaultVisible: readonly string[],
): ColumnState[] {
  const base: GridDefaultsSnapshot = savedAgainst ?? { version: 0, visible: [...pageDefaultVisible] };
  if (base.version === current.version) return [...saved];
  const wasVisible = new Set(base.visible);
  const newlyShown = new Set(current.visible.filter((id) => !wasVisible.has(id)));
  if (newlyShown.size === 0) return [...saved];
  return saved.map((column) => (newlyShown.has(column.colId) && column.hide ? { ...column, hide: false } : column));
}

/**
 * The default an admin saves from the grid in front of them: every data column
 * in on-screen order with its visibility and width. Labels set on the admin
 * screen are kept; the page's own header is recorded for that screen.
 */
export function defaultEntriesFromGrid(
  state: readonly ColumnState[],
  pageColumns: readonly ColDef<any>[],
  current: GridColumnDefault | null | undefined,
): GridColumnDefaultEntry[] {
  const headers = new Map<string, string>();
  for (const column of pageColumns) {
    const id = columnIdOf(column);
    if (id && typeof column.headerName === 'string' && column.headerName) headers.set(id, column.headerName);
  }
  const labels = new Map((current?.columns ?? []).filter((entry) => entry.label).map((entry) => [entry.colId, entry.label!]));
  return state
    .filter((column) => column.colId && !NON_DATA_COLUMN_IDS.has(column.colId) && headers.has(column.colId))
    .map((column) => {
      const entry: GridColumnDefaultEntry = { colId: column.colId, hide: Boolean(column.hide) };
      if (typeof column.width === 'number' && column.width > 0) entry.width = Math.round(column.width);
      const label = labels.get(column.colId);
      if (label) entry.label = label;
      entry.header = headers.get(column.colId);
      return entry;
    });
}
